<template>
  <o-table
    :items="rows"
    :loading="loading"
    :draggable="false"
    :stickyHeader="true"
    defaultSort="label"
    :selected="selectedRow"
    @select="$emit('select', $event.trackerId)"
  >
    <b-table-column v-slot="{ row }" sortable field="label" label="Tracker" cell-class="is-narrow">
      <router-link
        target="_blank"
        :to="{ name: 'queclinkTracker.edit', params: { id: row.trackerId, tab: 'statistics' } }"
      >
        {{ row.label }}
      </router-link>
    </b-table-column>
    <b-table-column
      v-slot="{ row }"
      sortable
      field="initialPercentage"
      label="Batterie initiale"
      :custom-sort="sortByNumber('initialPercentage')"
    >
      <span
        v-if="row.initialPercentage !== null"
        :class="{ 'has-text-danger': row.initialPercentage < lowInitialBatteryPercentage }"
      >
        {{ row.initialPercentage }} %
      </span>
      <template v-else>{{ EMPTY_VALUE }}</template>
    </b-table-column>
    <b-table-column
      v-slot="{ row }"
      sortable
      field="finalPercentage"
      label="Batterie finale"
      :custom-sort="sortByNumber('finalPercentage')"
    >
      <template v-if="row.finalPercentage !== null">{{ row.finalPercentage }} %</template>
      <template v-else>{{ EMPTY_VALUE }}</template>
    </b-table-column>
    <b-table-column
      v-slot="{ row }"
      sortable
      field="averageMotionConsumptionPerHour"
      label="Conso / h"
      :custom-sort="sortByNumber('averageMotionConsumptionPerHour')"
    >
      {{ formatRate(row.averageMotionConsumptionPerHour) }}
    </b-table-column>
    <b-table-column
      v-slot="{ row }"
      sortable
      field="averageIdleConsumptionPerHour"
      label="Conso veille / h"
      :custom-sort="sortByNumber('averageIdleConsumptionPerHour')"
    >
      {{ formatRate(row.averageIdleConsumptionPerHour) }}
    </b-table-column>
    <b-table-column
      v-slot="{ row }"
      sortable
      field="chargeCount"
      label="Recharges"
      :custom-sort="sortByNumber('chargeCount')"
    >
      {{ row.chargeCount === null ? EMPTY_VALUE : row.chargeCount }}
    </b-table-column>
    <b-table-column
      v-slot="{ row }"
      sortable
      field="averageMotionIntervalSeconds"
      label="Intervalle moyen"
      :custom-sort="sortByNumber('averageMotionIntervalSeconds')"
    >
      {{ formatInterval(row.averageMotionIntervalSeconds) }}
    </b-table-column>
    <template #empty>
      <div class="has-text-grey has-text-centered">
        {{ hasTrackers ? 'Aucune statistique calculée sur cette étape.' : 'Tracker introuvable.' }}
      </div>
    </template>
  </o-table>
</template>

<script>
import OTable from '@components/Table.vue'

const EMPTY_VALUE = '—'
const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 60 * 60

const formatDuration = (seconds) => {
  const rounded = Math.round(seconds)

  if (rounded < SECONDS_PER_MINUTE) {
    return `${rounded} s`
  }

  if (rounded < SECONDS_PER_HOUR) {
    return `${Math.floor(rounded / SECONDS_PER_MINUTE)} min`
  }

  const hours = Math.floor(rounded / SECONDS_PER_HOUR)
  const minutes = Math.floor((rounded % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)

  return `${hours} h ${String(minutes).padStart(2, '0')}`
}

export default {
  name: 'EditLiveReviewConsumptionTable',

  components: {
    OTable,
  },

  props: {
    rows: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    selectedTrackerId: {
      type: String,
      default: null,
    },
    lowInitialBatteryPercentage: {
      type: Number,
      required: true,
    },
    hasTrackers: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    selectedRow() {
      return this.rows.find((row) => row.trackerId === this.selectedTrackerId) || null
    },
  },

  watch: {
    selectedTrackerId: 'scrollToSelectedRow',
  },

  created() {
    this.EMPTY_VALUE = EMPTY_VALUE
  },

  methods: {
    sortByNumber(field) {
      return (left, right, isAscending) => {
        if (left[field] === null || right[field] === null) {
          return (left[field] === null ? 1 : 0) - (right[field] === null ? 1 : 0)
        }

        return isAscending ? left[field] - right[field] : right[field] - left[field]
      }
    },

    scrollToSelectedRow() {
      this.$nextTick(() => {
        const scroller = this.$el.querySelector('.table-wrapper')
        const row = this.$el.querySelector('tr.is-selected')
        if (!scroller || !row) {
          return
        }

        const scrollerBounds = scroller.getBoundingClientRect()
        const rowBounds = row.getBoundingClientRect()
        if (rowBounds.top >= scrollerBounds.top && rowBounds.bottom <= scrollerBounds.bottom) {
          return
        }

        scroller.scrollTop +=
          rowBounds.top - scrollerBounds.top - (scrollerBounds.height - rowBounds.height) / 2
      })
    },

    formatRate(percentagePerHour) {
      return percentagePerHour === null ? EMPTY_VALUE : `${percentagePerHour.toFixed(1)} %`
    },

    formatInterval(seconds) {
      return seconds === null ? EMPTY_VALUE : formatDuration(seconds)
    },
  },
}
</script>
